import {useCallback, useEffect, useRef} from 'react';
import {errorMessageKey} from '../../api';
import {useRouter} from '../../app/router';
import {QR_PARAMS, ROUTES} from '../../app/routes';
import {en, th, useI18n, zh} from '../../i18n';
import {useGuestSession, useServiceStatus} from '../../session';
import {
  Button,
  Screen,
  ScreenBody,
  ScreenFooter,
  SkeletonBar,
} from '../../ui';
import {LanguageToggle} from '../Q02LanguageToggle';

/**
 * # Q1 · Scan landing
 *
 * Route `/s?t=<table_qr_token>&m=<tenant_id>&b=<table_id>`.
 *
 * Turn a scanned QR into a working session with a table identity. The first
 * three seconds decide everything, so this screen does exactly four things:
 * name the venue, name the table, offer the three languages, and open the menu.
 *
 * ## What is deliberately not here
 *
 * **No image of any kind.** No venue logo, no hero photo, no wordmark SVG. The
 * contract carries no venue image field, most V1 merchants have no photography
 * (03 §constraints), and every asset is a round trip on 4G before the guest can
 * read anything. The venue's identity is carried by its **name, set in type** —
 * which is the no-photo case rendered as the only case, not as a fallback
 * branch. Nothing on this screen degrades if a picture fails to arrive, because
 * no picture was ever requested.
 *
 * **No field to fill in.** A guest asked to register leaves. There is not one
 * input on this screen and there must never be one.
 *
 * **No green.** Nothing here is money, so nothing here is Pandan. The selected
 * language is marked by `aria-pressed`, border and fill — never by a colour
 * alone, and never by the money colour.
 *
 * ## Hierarchy: the venue leads, pokpokPOS is subordinate
 *
 * 03 §Q1 leaves this open and recommends venue-leads; that is what this is. The
 * venue name is the `h1` at display size; pokpokPOS appears once, as body copy
 * in the footer (`common.attribution`). There is no lockup here or anywhere
 * (D-014, foundations §7) and the released slot is not reserved — the footer
 * line is the whole of it.
 *
 * ## REDESIGN · the shell, the type and the action are the shared ones now
 *
 * The design package does not draw this screen, so it is extrapolated from the
 * vocabulary of the nine it does draw: Sticky Rice ground, `Screen` / `ScreenBody`
 * / `ScreenFooter`, the two type steps plus the one intermediate title step, the
 * warm hairline, and exactly one solid Chili Red forward action sitting in an
 * unbordered footer — the shape the success screen uses, and for the same reason
 * (the content is short and centred, so a rule above the action reads as a
 * cut-off page). What was bespoke here — a hand-rolled primary button, a
 * hand-rolled skeleton box, a hand-rolled waiting block — is the shared
 * primitives' job and is no longer written twice.
 */

/**
 * Our word for "table", in every language this surface speaks.
 *
 * Derived from the dictionaries rather than typed here, for two reasons: a Thai
 * literal in a `.tsx` is what `test/guards.test.ts` exists to stop, and a word
 * copied out of `common.table` into a screen is a word that stops matching the
 * day somebody rewords the message. `'โต๊ะ {label}'` and `'{label} 号桌'` split
 * into `['โต๊ะ']` and `['号桌']` — note that Chinese puts it *after* the number,
 * which is why both ends are checked below.
 */
const TABLE_WORDS: readonly string[] = [th, en, zh]
  .flatMap((dictionary) => dictionary['common.table'].split('{label}'))
  .map((part) => part.trim())
  .filter((part) => part !== '');

/**
 * The merchant's label for a table, reduced to the identifier on the card.
 *
 * ## The bug this exists to end
 *
 * `table_label` is the merchant's own free text (C-1, D-023) and `common.table`
 * wraps it — `'โต๊ะ {label}'`. A merchant who names their tables `7` gets
 * "โต๊ะ 7", and a merchant who names them `โต๊ะ 7`, which is exactly what the
 * word printed on the laminated card says, gets **"โต๊ะ โต๊ะ 7"**. Both merchants
 * are doing something reasonable and one of them is reading a stutter on the
 * first screen of the product.
 *
 * ## Where the word belongs, decided once
 *
 * **The word is ours; the label is the identifier.** It is written in exactly one
 * place — `common.table` in the three dictionaries — because it has to be in the
 * *guest's* language, and the merchant types theirs in Thai. So a label that
 * already carries the word is normalised back to the identifier here, and the
 * sentence is composed once, localised, above.
 *
 * That also fixes a second case nobody had noticed: a Chinese guest at a table
 * the merchant called `โต๊ะ 7` was reading "โต๊ะ 7 号桌", which is the same stutter
 * in two scripts.
 *
 * A label that is *only* the word ("โต๊ะ", with no number) is left exactly as the
 * merchant typed it: stripping it to nothing would print "โต๊ะ " and tell the
 * guest less than the merchant did. Pure and total — every input returns a
 * string, and a UUID is never printed here in any branch.
 *
 * FOUNDATION · this belongs beside `common.table` in the shared layer, not in
 * Q1: the menu screen prints the same table line in its price-disclosure row and
 * has the same bug. Reported.
 */
export function bareTableLabel(label: string): string {
  let bare = label.trim();
  for (const word of TABLE_WORDS) {
    const lower = bare.toLocaleLowerCase();
    const needle = word.toLocaleLowerCase();
    if (lower.startsWith(needle)) bare = bare.slice(word.length);
    else if (lower.endsWith(needle)) bare = bare.slice(0, bare.length - word.length);
  }
  // Whatever joined the word to the number: a space, a dash, a colon, a hash.
  bare = bare.replace(/^[\s\-–—.:#]+|[\s\-–—.:#]+$/gu, '');
  return bare === '' ? label.trim() : bare;
}

export default function Q01ScanLanding() {
  const {t, ln, language} = useI18n();
  const {query, navigate} = useRouter();
  const {status, session, error, open} = useGuestSession();
  const {notServing, settled} = useServiceStatus();

  // Straight out of the printed QR, under the same names the request body uses
  // (D-019), so nothing here parses or reassembles anything.
  const token = query.get(QR_PARAMS.token)?.trim() ?? '';
  const tenantId = query.get(QR_PARAMS.tenant)?.trim() || undefined;
  const tableId = query.get(QR_PARAMS.table)?.trim() || undefined;

  // Which token this mount has already acted on. A scan is a POST that mints a
  // session; firing it twice — StrictMode, a re-render, a language change —
  // would leave an orphan session behind on the table.
  const opened = useRef<string | null>(null);

  const openFromQr = useCallback(() => {
    if (!token) return;
    opened.current = token;
    void open({
      tableQrToken: token,
      tenantId,
      tableId,
      serviceMode: 'dine_in',
      preferredLanguage: language,
    });
  }, [token, tenantId, tableId, language, open]);

  // Open on arrival — once per token, whatever the current status is. Not
  // `status === 'idle'`: a guest who moves to another table scans a *different*
  // token while a perfectly live session for the old one is still in storage,
  // and gating on idle would keep them ordering against the table they left.
  // `open()` itself resolves the common case (same token, live session) into a
  // resume with no request.
  useEffect(() => {
    if (!token || opened.current === token) return;
    openFromQr();
  }, [token, openFromQr]);

  // No token and nothing restored: this page was reached without a scan. Q19
  // says that in one line and tells them what to do (`q19.noQrTitle`).
  useEffect(() => {
    if (token || status !== 'idle') return;
    navigate(ROUTES.invalid, {replace: true});
  }, [token, status, navigate]);

  // The credential itself is the problem — the table's QR was regenerated, or
  // this is not a QR for this venue. Table QRs are regenerable if abused, so
  // this is a designed-for state, not an exception (03 §Q19). It can also land
  // *while the guest sits here*, when the provider's refresh comes back bad;
  // either way they are moved somewhere that tells them, never left on a page
  // whose "start ordering" button would fail.
  useEffect(() => {
    if (status === 'expired' || status === 'invalid') {
      navigate(ROUTES.invalid, {replace: true});
    }
  }, [status, navigate]);

  // Only the server's own `closed` / `outside_hours` sends a guest to Q20.
  // `useServiceStatus` returns a null verdict when the read is refused or fails,
  // and unknown is **not** closed: a closed notice in an open restaurant loses
  // the guest, which is a worse failure than a menu shown five minutes early.
  useEffect(() => {
    if (status === 'active' && notServing) {
      navigate(ROUTES.closed, {replace: true});
    }
  }, [status, notServing, navigate]);

  /*
   * A scan lands on the menu. Owner directive, 2026-08-14.
   *
   * This screen used to be a destination: welcome, venue name, table, language,
   * and a `เริ่มสั่งอาหาร` button. It is now a **junction** — it opens the
   * session, decides between the menu, Q19 and Q20, and gets out of the way.
   * A guest who has already performed the gesture of scanning has said what they
   * want; asking them to press a second button to say it again is a tap between
   * a hungry person and a menu.
   *
   * The approved prototype agrees, and did all along: its state machine opens on
   * `menu` and draws no landing at all. Q1 was one of five screens extrapolated
   * for the redesign because the package does not cover them — so this is less a
   * change of direction than the package finally being asked.
   *
   * **`settled`, not just `!notServing`.** A verdict that has not arrived is not
   * a verdict of "open": forwarding on it would paint the menu for one frame in
   * front of a guest standing outside a closed restaurant, then bounce them to
   * Q20. That is the exact flash `ready` below was written to avoid, and a
   * navigation is a louder version of it than a button.
   *
   * **`replace`, not push** — `router.tsx` says so in its own docblock: an entry
   * screen that resolves into the menu must replace, or the back gesture puts
   * the guest on a landing they never chose to visit, which then forwards them
   * again. Back from the menu now leaves the surface, which is correct: there is
   * nothing behind a scan.
   *
   * What the guest loses, and where it went: the venue name is in the menu's own
   * top bar, the table is in its subtitle, and the language control is in the top
   * bar of every screen (foundations §8) — so the language choice is not gone,
   * it is merely no longer a gate. What is genuinely gone is the pause: nobody
   * reads "ยินดีต้อนรับ" on the way to lunch.
   *
   * This screen keeps every other state. The loading and error branches below
   * are now the *only* things it ever renders, and they are the reason it still
   * exists — a scan that cannot reach the server has to say so somewhere.
   */
  useEffect(() => {
    if (status === 'active' && settled && !notServing) {
      navigate(ROUTES.menu, {replace: true});
    }
  }, [status, settled, notServing, navigate]);

  const venueName = session?.merchant_display_name ? ln(session.merchant_display_name) : '';
  // Whether the name may still be coming. `merchant_display_name` is optional in
  // the contract, so "the session landed and carries no name" is a legal, final
  // answer — and the skeleton below is a *loading* affordance, not a placeholder
  // for a field the venue never filled in. Once the session has resolved, an
  // `aria-busy` box that never fills tells a screen reader the page is still
  // working forever, and leaves a grey bar under the guest's thumb on a screen
  // that has finished.
  const nameStillComing = status === 'idle' || status === 'opening';
  // `session.table_label` is no longer read here — the table line moved to the
  // menu with the rest of this screen's content. `bareTableLabel` above stays
  // exported and stays this file's, because it is what stops "โต๊ะ โต๊ะ 7" and
  // its callers are now elsewhere.
  const failed = status === 'error';

  return (
    <Screen className="q1">
      <style href="q1-scan-landing" precedence="medium">
        {CSS}
      </style>

      <ScreenBody className="q1-body">
        <p className="pk-muted">{t('q1.welcome')}</p>

        {/*
          The venue name, the moment the session carries it — this is the
          "venue identity before the menu loads" requirement, and it is met by
          Q1 never reading the menu at all. The skeleton occupies the same box
          the name will, so the name lands without moving the page under a
          thumb already travelling toward the button.
        */}
        {venueName ? (
          <h1 className="pk-h1">{venueName}</h1>
        ) : nameStillComing ? (
          <h1 className="pk-h1" aria-busy="true">
            {/*
              `common.loading`, not `q1.openingSession` — the live region below
              already says the latter, and a screen reader that hears the same
              sentence twice in one screen stops trusting either of them.
            */}
            <span className="pokpok-sr-only">{t('common.loading')}</span>
            {/*
              Two lines' worth of the heading's own box, because Thai runs
              15-30% longer than English and most venue names wrap. The grey is
              `--color-skeleton`, which is what `index.html` has already been
              painting since before the bundle arrived.
            */}
            <SkeletonBar
              className="q1-venue-pending"
              width="70%"
              height="calc(var(--text-heading-1-size) * 1.3)"
            />
          </h1>
        ) : (
          // Resolved, and this venue has no display name. Nothing is drawn: the
          // heading is the venue's identity and there is no identity to set in
          // type, so `q1.welcome` above and the action below close up. Inventing
          // a name is not available, and neither is a permanent skeleton.
          null
        )}

        {/*
          The table, by the merchant's own name for it — the same string printed
          on the card the guest just scanned. This is Q1's whole "confirm you
          scanned the right QR" requirement, and until C-1 was approved
          (2026-08-09, D-023) it could not be met: `GuestSession` carried
          `table_id`, a UUID, and `listDiningTables` — which does carry
          `TableSummary.label` — correctly refuses a guest token, because that
          response also describes every other table in the room.

          `bareTableLabel` is what stops the doubled table word; see its own
          comment for where the word belongs, and why the sentence is composed
          here rather than carried in the merchant's data.

          `q1.tableUnknown` is still a real state and not a fallback for a
          missing field: a queue-ahead session is bound to no table, and saying
          so is the honest answer. **A UUID is never printed here.**
        */}
        {/*
          The table line used to sit here, and it has moved rather than gone: a
          resolved scan no longer renders this screen at all, and the menu's own
          subtitle carries the table (`MenuNote`, from the same `table_label`).
          Saying it here would mean showing it for one frame on the way past.
        */}

        <section className="q1-lang" aria-labelledby="q1-lang-heading">
          <h2 id="q1-lang-heading" className="q1-lang-heading">
            {t('q1.chooseLanguage')}
          </h2>
          {/*
            Offered while the session is still opening, not after. A guest who
            cannot read Thai cannot read the waiting state either, and the wait
            is exactly when they have nothing else to do. Switching re-renders
            and touches nothing else — no navigation, no remount, no request.

            Q02's segmented control: three pills in the guest's own script, one
            tap each. The link variant would be one tap fewer to draw and one
            navigation more to make, in a label a lost tourist cannot read.
          */}
          <LanguageToggle />
        </section>
      </ScreenBody>

      {/*
        No hairline above the footer and no top padding: the body is short and
        optically centred rather than scrolled under the bar, and the design
        draws the plain footer for exactly that case (the success and status
        screens) and the bordered one for the scrolling screens.
      */}
      <ScreenFooter bordered={false}>
        <div className="q1-action" aria-live="polite">
          {failed ? (
            <div className="q1-failure" role="alert">
              {/*
                Localised from `code`, never the server's English `message` —
                the contract is explicit that it is unstable operator text.
                Then what to do next, in one line (foundations §9).
              */}
              <p className="q1-failure-text">{t(errorMessageKey(error))}</p>
              <Button variant="primary" onClick={openFromQr}>
                {t('q1.retryOpening')}
              </Button>
              <p className="pk-hint q1-centred">{t('common.askStaff')}</p>
            </div>
          ) : (
            /*
              There is no "ready" branch any more. A resolved scan is forwarded
              to the menu by the effect above, so the only states this footer can
              be in are *failed* and *still working* — and the second one is what
              a guest sees for the length of one round trip, not something they
              are expected to act on.

              `q1.startOrdering` and `q1.orderFirstNote` are consequently unused
              on this screen. They are left in the dictionaries rather than
              deleted: `q1.orderFirstNote` is the pay-later sentence, which Q5 and
              Q7 both still show at the moment it becomes true, and a guest is
              better told about paying at the counter when they are about to
              order than on a screen they now pass through in under a second.
            */
            // Same height as the button it becomes, so nothing shifts. Not a
            // disabled button: a control that cannot be pressed yet invites the
            // press anyway, and this is the state where patience is the ask. A
            // card rather than a greyed fill, because `--pokpok-color-accent-disabled`
            // means "answer the question above me first" and there is no question.
            <p className="q1-opening">{t('q1.openingSession')}</p>
          )}
        </div>

        {/*
          pokpokPOS, once, as body copy. Present but subordinate — the guest is in
          a restaurant, not in a software product. No lockup, no wordmark asset,
          no processor disclosure: no money is shown on this screen.
        */}
        <p className="pk-hint q1-centred">{t('common.attribution')}</p>
      </ScreenFooter>
    </Screen>
  );
}

/*
 * Scoped to this screen and hoisted once by React.
 *
 * Nine rules, because everything else on the screen is now a shared primitive or
 * a shared class: the shell, the two type steps, the muted and hint tiers, the
 * button, the skeleton and the footer all come from `src/ui`. What is left is
 * this screen's own arrangement.
 *
 * Every value is a theme custom property, so this screen cannot drift from the
 * till's red — and two rules are visible in what is *absent*: no
 * `--pokpok-color-money-verified` anywhere (no money on this screen), and exactly
 * one solid `--color-accent` fill at a time, which lives in `Button` /
 * `ActionLink` and not here.
 *
 * Nothing is set below 16px. `--text-supporting-size` computes to 13.33px in the
 * theme and `global.css` pins it to the body size on this surface precisely so a
 * screen cannot reach for it; supporting copy is separated by colour and weight.
 */
const CSS = `
.q1 {
  max-width: 30rem;
  margin: 0 auto;
  width: 100%;
}

/* Optically centred rather than top-aligned: on the first screen of the product
   there is nothing above the fold to scroll to, and a short block pinned to the
   top of a tall phone reads as a page that failed to finish loading. */
.q1-body {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--pokpok-space-6);
}

/* The heading's box while the name is still in flight. A span is inline and
   would ignore the height it is given, so it is blockified here. */
.q1-venue-pending {
  display: block;
}

.q1-lang {
  display: flex;
  flex-direction: column;
  gap: var(--pokpok-space-2);
}

/* A label for the control under it, not a heading in the page's voice — so the
   body face rather than Kanit, and River Stone rather than ink. It is still an
   h2, because that is what makes the control findable by heading navigation. */
.q1-lang-heading {
  font-family: var(--font-body);
  font-size: var(--text-body-size);
  font-weight: 600;
  color: var(--color-text-secondary);
}

.q1-action {
  display: flex;
  flex-direction: column;
  gap: var(--pokpok-space-2);
}

.q1-failure {
  display: flex;
  flex-direction: column;
  gap: var(--pokpok-space-2);
}

.q1-failure-text {
  color: var(--color-error);
  font-weight: 600;
}

/* The button's box before there is a button. Card geometry, not button geometry:
   a warm hairline and the card ground say "not yet", where a greyed fill would
   say "you have not answered something". */
.q1-opening {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 56px;
  padding: 0 var(--pokpok-space-4);
  border: 1px solid var(--pokpok-color-border-warm);
  border-radius: var(--radius-container);
  background: var(--color-background-card);
  color: var(--color-text-secondary);
  text-align: center;
}

.q1-centred {
  text-align: center;
}
`;
